import { UserId, Uuid } from '../../models/Base';
import { GameType } from '../../models/GameStore';
import {
  ChallengeRecordJson,
  GameRecordJson,
  OpponentRecord,
  RevisionSchedule,
  RevisionScheduleJson,
} from '../../models/RecordStore';
import { Cursor } from '../../models/Repository';
import { PerformanceGameJson } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import {
  ModifiedPick,
  NonEmptyArray,
  Optional,
  PartialPick,
} from '../../models/Utils';
import {
  CursorData,
  CursorParams,
  ToAttachmentParams,
  UploadAttachmentData,
  WithProfilesData,
  WithProfilesParams,
} from '../utils';

import restClient, { RestRequestOptions, RestResponse } from '.';

export type SyncGameRecordParams = ModifiedPick<
  GameRecordJson,
  'classroomId' | 'gameSetId' | 'gameId',
  | 'gameCollectionId'
  | 'gameHash'
  | 'type'
  | 'slideshowId'
  | 'slideshowHash'
  | 'challengeRecordId'
  | 'sessionStartedAt'
  | 'startedAt'
  | 'buddyId'
  | 'opponentId'
  | 'endedAt'
  | 'durationInMs'
  | 'result'
  | 'score'
  | 'stars'
  | 'defeatedOpponents'
  | 'evaluation'
> & {
  index?: number;
  opponent?: OpponentRecord;
  friendChallengeGameRecordId?: string;
  // attachmentData
};

interface GameRecordData {
  gameRecord: GameRecordJson;
  challengeRecord?: ChallengeRecordJson;
}
export const syncGameRecords = (
  body: { gameRecords: NonEmptyArray<SyncGameRecordParams> },
  options?: RestRequestOptions,
): Promise<
  RestResponse<{ gameRecords: (GameRecordData | { errorMessage: string })[] }>
> => {
  return restClient.post('/classrooms/game-records', body, options);
};

interface GameRecordsData {
  gameRecords: GameRecordJson[];
}
export const getGameRecords = <
  P extends {
    playedBy?: UserId;
    gameSetId?: Uuid;
    gameId?: Uuid;
    type?: string;
    from?: string;
    to?: string;
    evaluation?: string;
    evaluated?: boolean;
    hash?: string; // for not getting cache from safari
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<GameRecordsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-records`,
    params,
    options,
  );
};

export const createGameRecord = (
  path: { classroomId: Uuid },
  body: ModifiedPick<
    GameRecordJson,
    'gameSetId' | 'gameId' | 'fee',
    'gameCollectionId' | 'gameHash' | 'type' | 'sessionStartedAt'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<GameRecordData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-records`,
    body,
    options,
  );
};

export const updateGameRecord = (
  path: { classroomId: Uuid; gameRecordId: Uuid },
  body: PartialPick<
    ToAttachmentParams<GameRecordJson>,
    | 'endedAt'
    | 'durationInMs'
    | 'result'
    | 'reward'
    | 'score'
    | 'stars'
    | 'defeatedOpponents'
    | 'evaluation'
  > = {},
  options?: RestRequestOptions,
): Promise<RestResponse<GameRecordData & UploadAttachmentData>> => {
  return restClient.put(
    `/classrooms/${path.classroomId}/game-records/${path.gameRecordId}`,
    body,
    options,
  );
};

export interface GameOpponentsData {
  opponents: (GameRecordJson | null)[];
}
export const getGameOpponents = <
  P extends {
    gameSetId: Uuid;
    gameId: Uuid;
    limit?: number;
    isAssisted?: boolean;
  } & Optional<WithProfilesParams>,
>(
  path: { classroomId: Uuid },
  params: P,
  options?: RestRequestOptions,
): Promise<RestResponse<GameOpponentsData & WithProfilesData<P>>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-opponents`,
    params,
    options,
  );
};

interface ChallengeRecordsData {
  challengeRecords: ChallengeRecordJson[];
}

export const getChallengeRecords = <
  P extends {
    gameSetId: Uuid;
    type?: string;
    mode?: string;
    hash?: string; // for not getting cache from safari
  } & CursorParams,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<ChallengeRecordsData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/challenge-records`,
    params,
    options,
  );
};

interface ChallengeRecordsData {
  challengeRecord: ChallengeRecordJson;
}

export const createChallengeRecord = (
  path: { classroomId: Uuid },
  body: Pick<ChallengeRecordJson, 'gameSetId' | 'type' | 'mode' | 'games'>,
  options?: RestRequestOptions,
): Promise<RestResponse<ChallengeRecordsData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/challenge-records`,
    body,
    options,
  );
};

type RevisionSchedulesData = {
  data: PerformanceGameJson;
  revisionSchedule: RevisionScheduleJson;
};

export interface GetRevisionSchedulesData {
  readyRevisionSchedules?: RevisionSchedulesData[];
  readyCursor?: Cursor;
  scheduledRevisionSchedules?: RevisionSchedulesData[];
  scheduledCursor?: Cursor;
  recentRevisionSchedules?: RevisionSchedulesData[];
}

export const getRevisionSchedules = <
  P extends {
    gameSetId?: Uuid;
    webBoosterType?: WebBoosterType | 'both';
    partitionId?: string;
    from?: string;
    to?: string;
    readyCursor?: Cursor;
    scheduleCursor?: Cursor;
  } & CursorParams,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<GetRevisionSchedulesData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/revision-schedules`,
    params,
    options,
  );
};

export interface GetRevisionScheduleData {
  revisionSchedule: RevisionSchedule;
}

export const getRevisionSchedule = (
  path: { classroomId: Uuid; gameId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<GetRevisionScheduleData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/revision-schedules/${path.gameId}`,
    {},
    options,
  );
};

export type RevisionGame = {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  gameId: Uuid;
  gameType: GameType;
};
export interface GetRevisionScheduleGameData {
  revisionGame: RevisionGame;
}

export const getRevisionScheduleGame = <
  P extends {
    gameSetId?: Uuid;
    webBoosterType?: WebBoosterType | 'both';
    partitionId?: string;
    from?: string;
    to?: string;
  },
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<GetRevisionScheduleGameData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/revision-game`,
    params,
    options,
  );
};
